import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import sessionManager from '../utils/sessionManager';
import axios from '../utils/axios';

function HubSpotTracking() {
  const account = useSelector((state) => state.account);
  const user = account?.user;
  const location = useLocation();

  useEffect(() => {
    // Load HubSpot tracking script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = '//js-na2.hs-scripts.com/242046969.js';
    
    // Check if script is already loaded
    if (!document.getElementById('hs-script-loader')) {
      document.body.appendChild(script);
    }

    // Clean up function
    return () => {
      // Don't remove the script on cleanup as HubSpot needs to persist
    };
  }, []);

  useEffect(() => {
    // Wait for HubSpot to be loaded
    const identifyUser = () => {
      if (user?.email && window._hsq) {
        // Push user identification to HubSpot
        window._hsq.push(['identify', {
          email: user.email,
          firstname: user.firstName || '',
          lastname: user.lastName || '',
          company: user.company || ''
        }]);
        
        // Track page view with user context
        window._hsq.push(['trackPageView']);
        
        // Convert anonymous activity for any user type (guest or customer)
        if (user.email) {
          convertAnonymousToKnown(user.email);
        }
      } else if (!user && window._hsq) {
        // Track anonymous users
        trackAnonymousPageView();
      }
    };

    // Try immediately if HubSpot is already loaded
    if (window._hsq) {
      identifyUser();
    } else {
      // Otherwise wait for HubSpot to load
      const checkInterval = setInterval(() => {
        if (window._hsq) {
          identifyUser();
          clearInterval(checkInterval);
        }
      }, 100);

      // Clean up interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);

      return () => clearInterval(checkInterval);
    }
  }, [user]);

  // Track route changes
  useEffect(() => {
    if (window._hsq) {
      window._hsq.push(['setPath', location.pathname]);
      window._hsq.push(['trackPageView']);
    }
    
    // Track anonymous page views (DISABLED - but keeping HubSpot's native tracking)
    if (!user) {
      // Note: Anonymous backend tracking is disabled, but HubSpot's native tracking still works
      trackAnonymousActivity({
        pageViewed: location.pathname,
        timestamp: new Date().toISOString()
      });
    }
  }, [location, user]);

  // Track anonymous page views
  const trackAnonymousPageView = () => {
    if (window._hsq) {
      window._hsq.push(['trackPageView']);
    }
  };

  // Track anonymous activity in backend
  // *** DISABLED *** - Anonymous tracking is currently disabled
  const trackAnonymousActivity = async (activityData) => {
    // DISABLED: Skip anonymous tracking
    return;
    
    /* DISABLED CODE - Keep for future reference
    try {
      const sessionId = sessionManager.getCurrentSessionId();
      if (sessionId) {
        await axios.post('/hubspot/anonymous/track', {
          sessionId,
          activityData
        });
      }
    } catch (error) {
      console.error('Failed to track anonymous activity:', error);
    }
    */
  };

  // Convert anonymous contact to known when user identifies
  const convertAnonymousToKnown = async (email) => {
    try {
      const sessionId = sessionManager.getCurrentSessionId();
      if (sessionId) {
        await axios.post('/hubspot/anonymous/convert', {
          sessionId,
          identifyingData: { email }
        });
        // Clear session after conversion
        sessionManager.clearSession();
      }
    } catch (error) {
      console.error('Failed to convert anonymous to known:', error);
    }
  };

  return null; // This component doesn't render anything
}

export default HubSpotTracking; 